/**
 * Image upload utility functions
 */

// Supported image formats
const SUPPORTED_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * Validates if the file is a supported image format and within size limits
 * @param {File} file - The file to validate
 * @returns {Object} - Validation result with success flag and error message
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { success: false, error: 'No file selected' };
  }

  if (!SUPPORTED_FORMATS.includes(file.type)) {
    return { 
      success: false, 
      error: `Unsupported file format. Please use: ${SUPPORTED_FORMATS.map(f => f.split('/')[1]).join(', ')}` 
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { 
      success: false, 
      error: `File size too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB` 
    };
  }

  return { success: true };
};

/**
 * Converts a file to a data URL
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Promise that resolves to the data URL
 */
export const fileToDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      resolve(event.target.result);
    };
    
    reader.onerror = (error) => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Handles the complete image upload process
 * @param {File} file - The file to upload
 * @param {Function} onSuccess - Callback function called on successful upload
 * @param {Function} onError - Callback function called on error
 * @param {Function} onProgress - Optional callback for upload progress
 */
export const handleImageUpload = async (file, onSuccess, onError, onProgress) => {
  try {
    // Validate the file
    const validation = validateImageFile(file);
    if (!validation.success) {
      onError(validation.error);
      return;
    }

    // Show progress if callback provided
    if (onProgress) {
      onProgress(0);
    }

    // Convert file to data URL
    const dataURL = await fileToDataURL(file);
    
    // Show completion progress
    if (onProgress) {
      onProgress(100);
    }

    // Call success callback with the data URL
    onSuccess(dataURL);
    
  } catch (error) {
    onError(error.message || 'Failed to upload image');
  }
};

/**
 * Creates a file input element and triggers file selection
 * @param {Function} onFileSelected - Callback function called when file is selected
 * @param {boolean} multiple - Whether to allow multiple file selection
 */
export const triggerFileSelection = (onFileSelected, multiple = false) => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = SUPPORTED_FORMATS.join(',');
  input.multiple = multiple;
  
  input.onchange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      if (multiple) {
        onFileSelected(Array.from(files));
      } else {
        onFileSelected(files[0]);
      }
    }
  };
  
  input.click();
};